import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import LoadingScreen from '../components/LoadingScreen';
import { InlineSelect } from '../components/InlineTable';
import { ConfigPageShell, ScopeSelect, ConfigEmptyState } from '../components/ConfigPages';
import BudgetCoaMappingView from '../components/BudgetCoaMappingView';
import RevenueTab from './revenueMap/RevenueTab';
import RoomStatsTab from './revenueMap/RoomStatsTab';
import { buildCoaMap, describeRevenueType, isBlank, isDni } from './revenueMap/revenueMapShared';
import {
  getRevenueTypes, getChartOfAccounts, getRoomStatTypes, getRevenueTypeBudgetAccounts, createRevenueTypeBudgetAccount,
  deleteRevenueTypeBudgetAccount, getTypeClassifications, getRoomStatAccountMappings, getAccountActivity,
} from '../utils/api';
import { getLocationsByOrgId } from '../utils/locationApi';
import { getFinancialBudgets } from '../utils/budgetApi';

const TABS = { revenue: 'revenue', roomStats: 'roomStats', budgetMapping: 'budgetMapping' };

const groupByKey = (rows, key) => {
  const out = {};
  (rows || []).forEach((r) => { (out[r[key]] = out[r[key]] || []).push(r); });
  return out;
};

/**
 * Revenue map — how each PMS revenue and stat type reaches the dashboards, P&L and budgets.
 * One shell, three tabs: Revenue (RevenueTab), Room stats (RoomStatsTab), Budget mapping
 * (BudgetCoaMappingView, unchanged inside). Data for the first two tabs loads together so tab
 * counts and the flow rail are right from the first paint.
 */
const RevenueTypesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrganization } = useUserSettingsContext();
  const flagEnabled = useFeatureFlagEnabled('revenue-map');
  const orgId = currentOrganization?.id;

  const [tab, setTab] = useState(TABS.revenue);
  const [locations, setLocations] = useState([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const [revenueTypes, setRevenueTypes] = useState([]);
  const [roomStatTypes, setRoomStatTypes] = useState([]);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [budgetMappingsData, setBudgetMappingsData] = useState({});   // { revenueTypeId: [mapping] }
  const [accountMappings, setAccountMappings] = useState({});         // { roomStatMappedTypeId: [mapping] }
  const [typeClassifications, setTypeClassifications] = useState([]);
  const [financialBudgets, setFinancialBudgets] = useState([]);
  const [accountActivity, setAccountActivity] = useState([]);
  const [budgetMappingYear, setBudgetMappingYear] = useState(new Date().getFullYear());

  const [initialLoading, setInitialLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const loadedOnce = useRef(false);

  // Reset the status filter when switching tabs — each tab has its own notion of "unmapped".
  useEffect(() => { setStatusFilter('all'); }, [tab]);

  // Global lookup (not org specific)
  useEffect(() => {
    getTypeClassifications().then((d) => setTypeClassifications(d || [])).catch((e) => console.error('Error fetching type classifications:', e));
  }, []);

  // Locations for the org (+ ?locationId deep link)
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLocationsLoaded(false);
    (async () => {
      try {
        const data = await getLocationsByOrgId(orgId);
        if (cancelled) return;
        setLocationsLoaded(true);
        if (Array.isArray(data) && data.length > 0) {
          setLocations(data);
          const urlLocationId = searchParams.get('locationId');
          if (urlLocationId) {
            setSelectedLocation(data.find((l) => String(l.id) === urlLocationId) || data[0]);
            setSearchParams({}, { replace: true });
          } else {
            setSelectedLocation((prev) => (prev && data.some((l) => l.id === prev.id) ? prev : data[0]));
          }
        } else {
          setLocations([]);
          setSelectedLocation(null);
          setInitialLoading(false);
        }
      } catch (err) {
        console.error('Error fetching locations:', err);
        toast.error('Could not load locations — reload the page.');
        setLocationsLoaded(true);
        setLocations([]);
        setSelectedLocation(null);
        setInitialLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const locationId = selectedLocation?.id;

  // Core data: revenue types, stat types (+mappings), COA, budget COAs, stat account sources
  const loadCore = useCallback(async () => {
    if (!orgId || !locationId) return;
    if (loadedOnce.current) setRefetching(true); else setInitialLoading(true);
    try {
      const [rts, rsts, coa, budget, acctMaps] = await Promise.all([
        getRevenueTypes(orgId, locationId, showDeleted),
        getRoomStatTypes(orgId, locationId, showDeleted, true),
        getChartOfAccounts(orgId, locationId),
        getRevenueTypeBudgetAccounts(orgId, locationId),
        getRoomStatAccountMappings(orgId, locationId),
      ]);
      setRevenueTypes(rts || []);
      setRoomStatTypes((rsts || []).map((t) => ({ ...t, mappings: (t.mappings || []).filter((m) => !m.isDeleted) })));
      setChartAccounts(coa || []);
      setBudgetMappingsData(groupByKey(budget, 'revenueTypeId'));
      setAccountMappings(groupByKey(acctMaps, 'roomStatMappedTypeId'));
      loadedOnce.current = true;
    } catch (err) {
      console.error('Error fetching revenue map data:', err);
      toast.error('Could not load the revenue map — reload the page.');
    } finally {
      setInitialLoading(false);
      setRefetching(false);
    }
  }, [orgId, locationId, showDeleted]);

  useEffect(() => { loadCore(); }, [loadCore]);

  const refreshRevenueTypes = useCallback(async () => {
    if (!orgId || !locationId) return;
    try { setRevenueTypes((await getRevenueTypes(orgId, locationId, showDeleted)) || []); } catch (e) { console.error(e); }
  }, [orgId, locationId, showDeleted]);

  const refreshRoomStatTypes = useCallback(async () => {
    if (!orgId || !locationId) return;
    try {
      const rsts = await getRoomStatTypes(orgId, locationId, showDeleted, true);
      setRoomStatTypes((rsts || []).map((t) => ({ ...t, mappings: (t.mappings || []).filter((m) => !m.isDeleted) })));
    } catch (e) { console.error(e); }
  }, [orgId, locationId, showDeleted]);

  // Budget-mapping tab extras (budgets for the chosen year + account activity decoration)
  useEffect(() => {
    if (tab !== TABS.budgetMapping || !orgId || !locationId) return;
    let cancelled = false;
    setBudgetLoading(true);
    Promise.all([
      getFinancialBudgets({ orgId, locationId, year: budgetMappingYear }),
      getAccountActivity(orgId, locationId, 'Revenue'), // swallows its own errors → []
    ]).then(([budgets, activity]) => {
      if (cancelled) return;
      setFinancialBudgets(budgets || []);
      setAccountActivity(activity || []);
    }).catch((err) => {
      if (cancelled) return;
      console.error('Error fetching budget mapping data:', err);
      toast.error('Could not load budgets for this year.');
    }).finally(() => { if (!cancelled) setBudgetLoading(false); });
    return () => { cancelled = true; };
  }, [tab, orgId, locationId, budgetMappingYear]);

  // ── Flow rail per tab ───────────────────────────────────────
  const coaMap = useMemo(() => buildCoaMap(chartAccounts), [chartAccounts]);
  const liveRevenue = useMemo(() => revenueTypes.filter((t) => !t.isDeleted), [revenueTypes]);
  const liveStats = useMemo(() => roomStatTypes.filter((t) => !t.isDeleted), [roomStatTypes]);

  const flow = useMemo(() => {
    const goUnmapped = () => setStatusFilter('unmapped');
    if (tab === TABS.revenue) {
      const total = liveRevenue.length;
      const withCoa = liveRevenue.filter((t) => t.chartOfAccountId != null).length;
      const noType = liveRevenue.filter((t) => isBlank(t.mappedType)).length;
      const dni = liveRevenue.filter((t) => isDni(t.mappedType)).length;
      const typed = total - noType;
      const stations = [
        { label: 'Revenue types', value: total.toLocaleString() },
        {
          label: 'Type bucket',
          value: `${typed.toLocaleString()} typed`,
          tone: typed > 0 ? 'ok' : 'neutral',
          sub: dni > 0 ? `${dni} DNI (excluded by decision)` : 'what the dashboards read',
        },
      ];
      if (noType > 0) stations.push({ label: 'Needs a type', value: noType.toLocaleString(), tone: 'warn', sub: 'excluded until typed', onClick: goUnmapped });
      stations.push({ label: 'GL account', value: `${withCoa.toLocaleString()} linked`, tone: withCoa > 0 ? 'neutral' : 'muted', sub: 'optional · P&L / GL lines' });
      stations.push({ label: 'Lands in', value: 'Dashboards · P&L', mono: false, sub: 'revenue by type · GL lines' });
      return { stations, coverage: { mapped: typed, total, label: 'with a type' } };
    }
    if (tab === TABS.roomStats) {
      const total = liveStats.length;
      const mapped = liveStats.filter((t) => (t.mappings || []).length > 0).length;
      const stations = [
        { label: 'Stat types', value: total.toLocaleString() },
        { label: 'Mapped stat', value: `${mapped.toLocaleString()} mapped`, tone: 'ok', sub: 'add · subtract · replace' },
      ];
      if (total - mapped > 0) stations.push({ label: 'Needs mapping', value: (total - mapped).toLocaleString(), tone: 'warn', onClick: goUnmapped });
      stations.push({ label: 'Lands in', value: 'Occupancy · ADR · RevPAR', mono: false });
      return { stations, coverage: { mapped, total, label: 'mapped' } };
    }
    const allMappings = Object.values(budgetMappingsData).flat();
    const accounts = new Set(allMappings.map((m) => m.chartOfAccountId)).size;
    const linkedTypes = new Set(allMappings.map((m) => m.revenueTypeId)).size;
    return {
      stations: [
        { label: 'Budget accounts', value: accounts.toLocaleString(), sub: 'GL accounts carrying a budget' },
        { label: 'Revenue type', value: `${linkedTypes.toLocaleString()} linked`, tone: 'ok' },
        { label: 'Lands in', value: 'Budget vs actual', mono: false, sub: 'dashboards · reports' },
      ],
    };
  }, [tab, liveRevenue, liveStats, coaMap, budgetMappingsData]);

  // ── Gates ───────────────────────────────────────────────────
  if (!currentOrganization) return <div className="p-4">Please select an organization first.</div>;
  if (!flagEnabled) return <div className="p-4">This feature is disabled.</div>;
  if (!locationsLoaded || (initialLoading && locationId)) return <LoadingScreen message="Loading revenue map" />;

  const locationOptions = locations.map((l) => ({ value: l.id, label: l.name }));
  const tabs = [
    { key: TABS.revenue, label: 'Revenue', count: liveRevenue.length },
    { key: TABS.roomStats, label: 'Room stats', count: liveStats.length },
    { key: TABS.budgetMapping, label: 'Budget mapping' },
  ];

  return (
    <ConfigPageShell
      title="Revenue map"
      subtitle="How each PMS revenue and stat type reaches the dashboards, P&L and budgets."
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      scope={locations.length > 0 ? (
        <ScopeSelect>
          <InlineSelect
            rowActive
            clearable={false}
            value={locationId ?? null}
            options={locationOptions}
            onChange={(v) => setSelectedLocation(locations.find((l) => l.id === v) || null)}
            placeholder="Select location…"
            minWidth={220}
            ariaLabel="Location"
          />
        </ScopeSelect>
      ) : null}
      flow={locationId ? flow : null}
    >
      {!locationId ? (
        <div className="it-frame">
          <ConfigEmptyState title="Select a location" body="Revenue and stat types are mapped per location. Pick one above to see its map." />
        </div>
      ) : tab === TABS.revenue ? (
        <RevenueTab
          orgId={orgId}
          locationId={locationId}
          revenueTypes={revenueTypes}
          setRevenueTypes={setRevenueTypes}
          chartAccounts={chartAccounts}
          budgetMappingsData={budgetMappingsData}
          setBudgetMappingsData={setBudgetMappingsData}
          typeClassifications={typeClassifications}
          showDeleted={showDeleted}
          setShowDeleted={setShowDeleted}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          refreshRevenueTypes={refreshRevenueTypes}
          busy={refetching}
        />
      ) : tab === TABS.roomStats ? (
        <RoomStatsTab
          orgId={orgId}
          locationId={locationId}
          roomStatTypes={roomStatTypes}
          setRoomStatTypes={setRoomStatTypes}
          accountMappings={accountMappings}
          setAccountMappings={setAccountMappings}
          chartAccounts={chartAccounts}
          showDeleted={showDeleted}
          setShowDeleted={setShowDeleted}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          refreshRoomStatTypes={refreshRoomStatTypes}
          busy={refetching}
        />
      ) : (
        <BudgetCoaMappingView
          chartAccounts={chartAccounts}
          revenueTypes={liveRevenue}
          budgetMappingsData={budgetMappingsData}
          financialBudgets={financialBudgets}
          accountActivity={accountActivity}
          selectedYear={budgetMappingYear}
          onYearChange={setBudgetMappingYear}
          orgId={orgId}
          locationId={locationId}
          currency={selectedLocation?.currency || 'USD'}
          onCreateMapping={async (revenueTypeId, chartOfAccountId) => {
            const created = await createRevenueTypeBudgetAccount({ revenueTypeId, chartOfAccountId, orgId, locationId });
            setBudgetMappingsData((prev) => ({ ...prev, [revenueTypeId]: [...(prev[revenueTypeId] || []), created] }));
            toast.success('Budget mapping added');
          }}
          onDeleteMapping={async (mappingId, revenueTypeId) => {
            setBudgetMappingsData((prev) => ({ ...prev, [revenueTypeId]: (prev[revenueTypeId] || []).filter((m) => m.id !== mappingId) }));
            await deleteRevenueTypeBudgetAccount(mappingId);
            toast.success('Budget mapping removed');
          }}
          loading={refetching || budgetLoading}
        />
      )}
    </ConfigPageShell>
  );
};

export default RevenueTypesPage;
